import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { values as enumValues } from "lodash";
import ReactSelect, { type MultiValue, type SingleValue } from "react-select";
import {
  RateType,
  TariffGroupType,
  TariffType,
  TariffZoneType,
} from "../../../../generated/graphql";
import type { BulkBinding } from "./bulk-types";

const styles = {
  fullWidth: { width: "100%" },
} satisfies Record<string, SxProps<Theme>>;

type Option = { value: string; label: string };

type BulkDefaultsPanelProps = {
  readonly defaults: BulkBinding;
  readonly serviceOptions: Option[];
  readonly vehicleTypeOptions: Option[];
  readonly fuelProfiles: Array<{ uuid: string; name?: string | null }>;
  readonly tariffZoneGroups: Array<{ id: string; name: string }>;
  readonly rowCount: number;
  readonly onChange: (patch: Partial<BulkBinding>) => void;
  readonly onApplyToAll: () => void;
};

/**
 * Renders the shared-defaults panel for bulk import: the binding fields that are
 * usually constant across a batch (services, vehicle types, fuel profile, dates,
 * tariff/zone/rate/group types, use-actual-weight, zone group). New rows are
 * seeded from these defaults; "Apply to all rows" pushes the current defaults
 * onto every existing non-imported row (name and customers are left untouched,
 * since those are per-row).
 *
 * @param props.defaults The current default binding.
 * @param props.serviceOptions Selectable service options.
 * @param props.vehicleTypeOptions Selectable vehicle-type options.
 * @param props.fuelProfiles Fuel profile options.
 * @param props.tariffZoneGroups Zone group options.
 * @param props.rowCount Number of rows currently loaded (for the apply button).
 * @param props.onChange Patches the defaults.
 * @param props.onApplyToAll Pushes defaults onto all existing rows.
 */
export const BulkDefaultsPanel = ({
  defaults,
  serviceOptions,
  vehicleTypeOptions,
  fuelProfiles,
  tariffZoneGroups,
  rowCount,
  onChange,
  onApplyToAll,
}: BulkDefaultsPanelProps) => {
  const selectedServices = new Set(defaults.serviceUuids);
  const selectedVehicleTypes = new Set(defaults.vehicleTypeUuids);
  return (
    <Grid container spacing={2}>
      <Grid item xs={2}>
        <InputLabel>Group type</InputLabel>
        <Select
          size="small"
          sx={styles.fullWidth}
          value={defaults.tariffGroupType}
          onChange={(event) => {
            onChange({
              tariffGroupType: event.target.value as TariffGroupType,
            });
          }}
        >
          {enumValues(TariffGroupType).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={2}>
        <InputLabel>Default zone type</InputLabel>
        <Select
          size="small"
          sx={styles.fullWidth}
          value={defaults.tariffZoneType}
          onChange={(event) => {
            onChange({ tariffZoneType: event.target.value as TariffZoneType });
          }}
        >
          {enumValues(TariffZoneType).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={2}>
        <InputLabel>Unit type</InputLabel>
        <Select
          size="small"
          sx={styles.fullWidth}
          value={defaults.tariffType}
          onChange={(event) => {
            onChange({ tariffType: event.target.value as TariffType });
          }}
        >
          {enumValues(TariffType).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={2}>
        <InputLabel>Rate type</InputLabel>
        <Select
          size="small"
          sx={styles.fullWidth}
          value={defaults.rateType}
          onChange={(event) => {
            onChange({ rateType: event.target.value as RateType });
          }}
        >
          {enumValues(RateType).map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={2}>
        <InputLabel>Start date</InputLabel>
        <TextField
          fullWidth
          size="small"
          type="date"
          value={defaults.startDate ?? ""}
          onChange={(event) => {
            onChange({
              startDate: event.target.value === "" ? null : event.target.value,
            });
          }}
        />
      </Grid>
      <Grid item xs={2}>
        <InputLabel>End date</InputLabel>
        <TextField
          fullWidth
          size="small"
          type="date"
          value={defaults.endDate ?? ""}
          onChange={(event) => {
            onChange({
              endDate: event.target.value === "" ? null : event.target.value,
            });
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <InputLabel>Services</InputLabel>
        <ReactSelect
          isMulti
          menuPosition="fixed"
          isDisabled={defaults.autoSelectAllServices}
          options={serviceOptions}
          value={serviceOptions.filter((option) =>
            selectedServices.has(option.value),
          )}
          placeholder="Select services"
          onChange={(option: MultiValue<Option>) => {
            onChange({ serviceUuids: option.map((item) => item.value) });
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={defaults.autoSelectAllServices}
              onChange={(event) => {
                onChange({ autoSelectAllServices: event.target.checked });
              }}
            />
          }
          label="All services"
        />
      </Grid>
      <Grid item xs={4}>
        <InputLabel>Vehicle types</InputLabel>
        <ReactSelect
          isMulti
          menuPosition="fixed"
          isDisabled={defaults.autoSelectAllVehicleTypes}
          options={vehicleTypeOptions}
          value={vehicleTypeOptions.filter((option) =>
            selectedVehicleTypes.has(option.value),
          )}
          placeholder="Select vehicle types"
          onChange={(option: MultiValue<Option>) => {
            onChange({ vehicleTypeUuids: option.map((item) => item.value) });
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={defaults.autoSelectAllVehicleTypes}
              onChange={(event) => {
                onChange({ autoSelectAllVehicleTypes: event.target.checked });
              }}
            />
          }
          label="All vehicle types"
        />
      </Grid>
      <Grid item xs={4}>
        <InputLabel>Fuel profile</InputLabel>
        <Select
          size="small"
          sx={styles.fullWidth}
          value={defaults.fuelProfileUuid ?? ""}
          onChange={(event) => {
            onChange({
              fuelProfileUuid:
                event.target.value === "" ? null : event.target.value,
            });
          }}
        >
          {fuelProfiles.map((fuelProfile) => (
            <MenuItem key={fuelProfile.uuid} value={fuelProfile.uuid}>
              {fuelProfile.name}
            </MenuItem>
          ))}
        </Select>
        <InputLabel>Zone group</InputLabel>
        <ReactSelect
          isClearable
          menuPosition="fixed"
          options={tariffZoneGroups.map((group) => ({
            value: group.id,
            label: group.name,
          }))}
          value={
            tariffZoneGroups
              .filter((group) => group.id === defaults.tariffZoneGroupId)
              .map((group) => ({ value: group.id, label: group.name }))[0] ??
            null
          }
          placeholder="Select zone group"
          onChange={(option: SingleValue<Option>) => {
            onChange({ tariffZoneGroupId: option?.value ?? null });
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={defaults.useActualWeight}
              onChange={(event) => {
                onChange({ useActualWeight: event.target.checked });
              }}
            />
          }
          label="Use actual weight"
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="outlined"
          disabled={rowCount === 0}
          onClick={onApplyToAll}
        >
          Apply defaults to all {rowCount} rows
        </Button>
      </Grid>
    </Grid>
  );
};
